import { useEffect } from "react";

function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}

/** Piecewise linear fade between scroll progress anchors. */
function ramp(progress, start, end, from = 0, to = 1) {
  if (progress <= start) return from;
  if (progress >= end) return to;
  const t = (progress - start) / (end - start);
  return from + t * (to - from);
}

function computeHeroScrollStyles(progress) {
  const p = clamp01(progress);

  return {
    "--ds-hero-p": String(p),
    "--ds-hero-en-title-o": String(ramp(p, 0.1, 0.4, 1, 0)),
    "--ds-hero-kr-title-o": String(ramp(p, 0.1, 0.4, 0, 1)),
    "--ds-hero-en-tagline-o": String(ramp(p, 0.08, 0.38, 1, 0)),
    "--ds-hero-kr-tagline-o": String(ramp(p, 0.08, 0.38, 0, 1)),
    "--ds-hero-en-cta-o": String(ramp(p, 0.08, 0.38, 1, 0)),
    "--ds-hero-kr-cta-o": String(ramp(p, 0.08, 0.38, 0, 1)),
    /* No whole-hero fade or exit scrim — those caused sudden black screen */
    "--ds-hero-sticky-o": "1",
    "--ds-hero-exit-scrim-o": "0",
    "--ds-hero-hint-o": String(ramp(p, 0.72, 0.92, 1, 0)),
    "--ds-hero-hint-intro-o": String(ramp(p, 0.14, 0.34, 1, 0)),
    "--ds-hero-hint-kr-o": String(ramp(p, 0.14, 0.34, 0, 1) * ramp(p, 0.72, 0.88, 1, 0)),
    "--ds-hero-hint-exit-o": String(ramp(p, 0.72, 0.88, 0, 1)),
  };
}

function measureTrackProgress(trackEl) {
  const scrollable = trackEl.offsetHeight - window.innerHeight;
  if (scrollable <= 0) return 0;
  return clamp01(-trackEl.getBoundingClientRect().top / scrollable);
}

/**
 * Scroll-linked hero opacities via CSS variables — no React re-renders while scrolling.
 */
export function useMainHeroScrollProgress(trackRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const trackEl = trackRef.current;
    if (!trackEl) return undefined;

    let rafId = 0;
    let lastSnapshot = "";

    const apply = () => {
      rafId = 0;
      const styles = computeHeroScrollStyles(measureTrackProgress(trackEl));
      const snapshot = Object.values(styles).join("|");
      if (snapshot === lastSnapshot) return;
      lastSnapshot = snapshot;

      for (const [key, value] of Object.entries(styles)) {
        trackEl.style.setProperty(key, value);
      }
    };

    const schedule = () => {
      if (!rafId) rafId = requestAnimationFrame(apply);
    };

    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [enabled, trackRef]);
}
